"use strict";
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExpandoWidget = exports.ExpandoDirection = void 0;
var tslib_1 = require("tslib");
var ArrowChevronUpIcon_1 = require("@tableau/icons/ArrowChevronUpIcon");
var React = tslib_1.__importStar(require("react"));
var TypeStyle = tslib_1.__importStar(require("typestyle"));
var Icons_1 = require("../../icons/Icons");
var Colors_1 = require("../../styles/Colors");
var Animation_1 = require("../../utils/Animation");
var Declarative_1 = require("../../utils/Declarative");
var DomUtil_1 = require("../../utils/DomUtil");
var HeightAnimator_1 = require("../../utils/HeightAnimator");
var ReactHelpers_1 = require("../../utils/ReactHelpers");
var ExpandoStyles_1 = require("./ExpandoStyles");
var ExpandoDirection;
(function (ExpandoDirection) {
    ExpandoDirection[ExpandoDirection["Up"] = 0] = "Up";
    ExpandoDirection[ExpandoDirection["Down"] = 1] = "Down";
})(ExpandoDirection = exports.ExpandoDirection || (exports.ExpandoDirection = {}));
// In the collapsed state, caret (which originally points up) either points right or left
var CollapsedIconTransform = Object.freeze({
    left: 'rotate(90deg)',
    right: 'rotate(-90deg)',
});
// In the expanded state, caret (which originally points up) either points up or down
var ExpandedIconTransform = Object.freeze((_a = {},
    _a[ExpandoDirection.Up + "left"] = 'rotate(0deg)',
    _a[ExpandoDirection.Up + "right"] = 'rotate(0deg)',
    _a[ExpandoDirection.Down + "left"] = 'rotate(180deg)',
    _a[ExpandoDirection.Down + "right"] = 'rotate(-180deg)',
    _a));
var ExpandoBaseTestId = 'Expando';
/**
 * Expands/collapses to show/hide content. Can be used as either a controlled or uncontrolled component.
 *
 * In controlled mode, the parent is responsible for controlling expansion state via the `isExpanded` prop, signaled by the `onCollapse` and `onExpand` props.
 *
 * In uncontrolled mode, the state is handled internally by the Expando, and can be defaulted using the `isInitiallyExpanded` prop.
 *
 * **NOTE**: all HTML props are applied to the focusable button element, with the exception of `style` and `className`, which are applied
 * to the root wrapper element.
 */
exports.ExpandoWidget = React.forwardRef(function (_a, ref) {
    var _b, _c;
    var alignment = _a.alignment, children = _a.children, className = _a.className, direction = _a.direction, expandoType = _a.expandoType, isControlledExpanded = _a.isExpanded, isInitiallyExpanded = _a.isInitiallyExpanded, label = _a.label, labelIcon = _a.labelIcon, keepChildrenMounted = _a.keepChildrenMounted, noBackgroundWhenExpanded = _a.noBackgroundWhenExpanded, onCollapse = _a.onCollapse, onExpand = _a.onExpand, style = _a.style, subLabel = _a.subLabel, testId = _a.testId, htmlAttrs = tslib_1.__rest(_a, ["alignment", "children", "className", "direction", "expandoType", "isExpanded", "isInitiallyExpanded", "label", "labelIcon", "keepChildrenMounted", "noBackgroundWhenExpanded", "onCollapse", "onExpand", "style", "subLabel", "testId"]);
    var isUncontrolledMode = isControlledExpanded === undefined;
    var _d = React.useState(isInitiallyExpanded), isUncontrolledExpanded = _d[0], setExpanded = _d[1];
    ReactHelpers_1.useEffectExceptOnMount(function () { var _a; return (_a = (isUncontrolledExpanded ? onExpand : onCollapse)) === null || _a === void 0 ? void 0 : _a(); }, [isUncontrolledExpanded]);
    var isExpanded = isUncontrolledMode ? isUncontrolledExpanded : isControlledExpanded;
    var toggle = function (expanded) {
        var _a;
        if (expanded === void 0) { expanded = !isExpanded; }
        if (isUncontrolledMode) {
            setExpanded(expanded);
        }
        else {
            (_a = (expanded ? onExpand : onCollapse)) === null || _a === void 0 ? void 0 : _a();
        }
    };
    React.useImperativeHandle(ref, function () { return ({ toggle: toggle }); });
    var contentId = React.useState(DomUtil_1.generateId())[0];
    var contentHeight = isExpanded ? 'auto' : 0;
    var expandoIconStyle = tslib_1.__assign(tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, Icons_1.IconStyle), { transform: isExpanded ?
            ExpandedIconTransform["" + direction + alignment] :
            // collapsed state only cares about alignment, not direction
            CollapsedIconTransform[alignment], transition: "transform " + Animation_1.AnimationDuration + "ms" }), (label || labelIcon) && {
        // add some margin when there is another element right next to the expando icon
        marginLeft: alignment === 'right' ? 8 : 0,
        marginRight: alignment === 'left' ? 8 : 0,
    }), (alignment === 'right') && { order: 1 });
    var cssFlags = (_b = {},
        _b[alignment + "-align"] = true,
        _b[(direction === ExpandoDirection.Up ? 'up' : 'down') + "-direction"] = true,
        _b.hasLeftBorder = expandoType !== 'default' && expandoType !== 'neutral',
        _b.noBackgroundWhenExpanded = noBackgroundWhenExpanded,
        _b);
    return (React.createElement("div", { className: TypeStyle.classes(className, ExpandoStyles_1.ExpandoColorStyleNames[expandoType], ExpandoStyles_1.ExpandoStyleNames.rootStyleName, (_c = {},
            _c[ExpandoStyles_1.ExpandoStyleNames.legacyAlignmentStyleName] = expandoType === 'default' && alignment === 'left',
            _c)), "data-tb-test-id": Declarative_1.makeTestId(testId, ExpandoBaseTestId), style: style },
        React.createElement("button", tslib_1.__assign({}, ReactHelpers_1.EventUtilsOptOut, { "data-tb-test-id": Declarative_1.makeTestId(testId, ExpandoBaseTestId, 'Button') }, htmlAttrs, (isExpanded && { 'aria-controls': contentId }), { "aria-expanded": isExpanded, className: TypeStyle.classes(ExpandoStyles_1.ExpandoStyleNames.buttonStyleName, cssFlags), onClick: function () { return toggle(); } }),
            React.createElement("span", { key: 'label' },
                React.createElement(ArrowChevronUpIcon_1.ArrowChevronUpIcon, { key: 'expandoIcon', "aria-hidden": true, color: Colors_1.TextColors.Primary, "data-tb-test-id": Declarative_1.makeTestId(testId, ExpandoBaseTestId, 'Icon'), size: 14, style: expandoIconStyle }),
                labelIcon &&
                    React.createElement("span", { className: ExpandoStyles_1.ExpandoStyleNames.labelIconStyleName, "data-tb-test-id": Declarative_1.makeTestId(testId, ExpandoBaseTestId, 'LabelIcon') }, labelIcon),
                label &&
                    React.createElement("span", { key: 'expandoLabel', className: ExpandoStyles_1.ExpandoStyleNames.labelStyleName, "data-tb-test-id": Declarative_1.makeTestId(testId, ExpandoBaseTestId, 'Label') }, label)),
            subLabel &&
                React.createElement("span", { key: 'sublabel', className: ExpandoStyles_1.ExpandoStyleNames.subLabelStyleName, "data-tb-test-id": Declarative_1.makeTestId(testId, ExpandoBaseTestId, 'SubLabel') }, subLabel)),
        React.createElement(HeightAnimator_1.HeightAnimator, tslib_1.__assign({ key: 'content' }, (keepChildrenMounted && { height: contentHeight }), { initialHeight: contentHeight }), (keepChildrenMounted || isExpanded) && React.createElement("div", { className: TypeStyle.classes(ExpandoStyles_1.ExpandoStyleNames.contentStyleName, cssFlags), "data-tb-test-id": Declarative_1.makeTestId(testId, ExpandoBaseTestId, 'Content'), id: contentId }, children))));
});
exports.ExpandoWidget.defaultProps = { alignment: 'left', direction: ExpandoDirection.Down, expandoType: 'default' };
exports.ExpandoWidget.displayName = 'ExpandoWidget';
//# sourceMappingURL=ExpandoWidget.js.map